// modifyJoinOn — VQE-Edit edit-action: replace the ON predicate of a JOIN
// clause via SR-Edit's `modify-join-on` op. Builds the StructuralEdit and
// delegates to `commitEdit`. Empty / whitespace-only `newOnText` is rejected;
// callers should remove the JOIN entirely or switch its kind to CROSS to
// drop the predicate.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN +
// edit ON predicate.

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx } from '../commitEdit';

export type ModifyJoinOnOptions = {
  /**
   * Optional structure used for index-bounds validation. Lets the caller
   * surface a friendlier error than SR-Edit's silent drop.
   */
  readonly structure?: QueryStructure;
};

export async function modifyJoinOn(
  index: number,
  newOnText: string,
  ctx: CommitEditCtx,
  options: ModifyJoinOnOptions = {},
): Promise<void> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`modifyJoinOn: index must be a non-negative integer (got ${index})`);
  }
  const trimmed = newOnText.trim();
  if (trimmed.length === 0) {
    throw new Error('modifyJoinOn: newOnText cannot be empty');
  }
  if (options.structure) {
    const joins = options.structure.joins ?? [];
    if (index >= joins.length) {
      throw new Error(
        `modifyJoinOn: index ${index} out of range (JOIN list has ${joins.length} item(s))`,
      );
    }
    const target = joins[index];
    if (!target?.on || target.on.length === 0) {
      throw new Error(
        `modifyJoinOn: JOIN at index ${index} has no ON predicate to modify (use add-join with onText to set one)`,
      );
    }
  }
  await commitEdit({ op: 'modify-join-on', index, newOnText: trimmed }, ctx);
}
